import { Form, Modal } from "antd";
import TextArea from "antd/es/input/TextArea";
import i18n from "i18next";
import { type FC, useCallback } from "react";

import type { SiteEntity } from "src/app/features/Site/types/SiteEntity";
import { ActionBar } from "../../../../../components/ActionBar/ActionBar";
import { PrimaryButton, SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import { CsvImportModalButton } from "../../../../../components/FeatureButtons/CsvImportModalButton";
import { RenderImportResult } from "../../../../../components/FeatureButtons/csvModalButtonUtils";
import type { ButtonProps } from "../../../../../components/FeatureButtons/types";
import { Checkbox, SelectDynamic } from "../../../../../components/Form/formComponents";
import { ProgressBar } from "../../../../../components/ProgressBar/ProgressBar";
import { dataGroupSelectorConfig } from "../../../../../components/SearchForm/FormSearchUtils";
import { useMoveSiteModalLogic } from "./useMoveSiteModalLogic";

export const BatchMoveSiteModal: FC<ButtonProps<SiteEntity>> = ({ isDisabled }) => {
    const [form] = Form.useForm();

    const [modalState, callbacks] = useMoveSiteModalLogic(form, {
        formatErrors: false,
        errorMessageKey: "batchMoveData.modal.importError",
        isBatch: true,
    });

    const { isModalVisible, uploading, importResult, showAllErrors } = modalState;
    const { showModal, onCancel, onOk } = callbacks;

    const handleOk = async () => {
        const values = await form.validateFields();
        await onOk(async () => ({
            orgUnitIds: values.siteIds,
            dataGroupId: Number(values.destinationDataGroupId),
            useTargetSGValues: values.targetSiteGroupValues ?? true,
        }));
    };

    const onCsvContent = useCallback(
        (content: string) => {
            if (content) {
                form.setFieldsValue({ siteIds: content });
            }
        },
        [form],
    );

    return (
        <>
            <SecondaryButton onClick={showModal} disabled={isDisabled}>
                {i18n.t("button.batchMove")}
            </SecondaryButton>

            <Modal
                open={isModalVisible}
                title={i18n.t("batchMoveData.modal.title")}
                onOk={handleOk}
                onCancel={onCancel}
                footer={[
                    <SecondaryButton key="close" onClick={onCancel}>
                        {i18n.t("common.close")}
                    </SecondaryButton>,
                    importResult?.importSuccessCount ? null : (
                        <PrimaryButton key="import" onClick={handleOk} disabled={uploading}>
                            {i18n.t("button.move")}
                        </PrimaryButton>
                    ),
                ]}
            >
                <Form form={form}>
                    <ActionBar className="column">
                        <p>{i18n.t("moveData.modal.message")}</p>
                        <Form.Item
                            name="destinationDataGroupId"
                            rules={[{ required: true, message: i18n.t("batchMoveData.error.dataGroupRequired") }]}
                        >
                            <SelectDynamic
                                fieldConfig={dataGroupSelectorConfig}
                                name="destinationDataGroupId"
                                label={i18n.t("moveData.dataGroup")}
                            />
                        </Form.Item>
                        <Form.Item name="siteIds" rules={[{ required: true, message: i18n.t("batchMoveData.error.textAreaRequired") }]}>
                            <TextArea rows={4} placeholder={i18n.t("batchMoveData.modal.textAreaPlaceholder")} name="siteIds" />
                        </Form.Item>
                        <CsvImportModalButton callback={onCsvContent} />
                        {uploading && <ProgressBar />}
                        <Form.Item name="targetSiteGroupValues" valuePropName="checked">
                            <Checkbox
                                label="orgUnitMove.targetSiteGroupValues"
                                readOnly={false}
                                defaultChecked={true}
                                name="targetSiteGroupValues"
                            />
                        </Form.Item>
                        {importResult && (
                            <RenderImportResult
                                importResult={importResult}
                                showAllErrors={showAllErrors}
                                setShowAllErrors={modalState.setShowAllErrors}
                                successString="moveData.successString"
                            />
                        )}
                    </ActionBar>
                </Form>
            </Modal>
        </>
    );
};
